import { useGetAlbumVideos } from '@vimeo/gctl-api/hooks/useAlbumVideos';
import { useViewer } from '@vimeo/viewer';
import { useBaseUrl } from '@vimeo/viewer-showcase-ui/hooks/use-base-url';
import { getEventIdFromClip } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useGetShowcase } from './use-get-showcase';
import { useAlbumStore } from '../state/albumStore';
import { SELECT_ALBUM_FIELDS } from '../utils/constants';
import { contextUrlParser } from '../utils/helper';

const DEFAULT_SHOWCASE_QUERY = {
  forceCollectionUri: true,
  sizes: ['640x360', '360x203'],
};

export const useGetFirstPlaylistVideo = (shouldGetFirstVideo: boolean) => {
  const { albumId, password, isShowcaseEmbedded, referrer } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    referrer: state.referrer,
  }));

  const viewer = useViewer();

  const embedQuery = {
    isEmbed: isShowcaseEmbedded,
    referrer,
  };

  /** Additional query params to send along API request */
  const { sortConfig, hideUpcoming } = useGetShowcase();
  const baseUrl = useBaseUrl();

  const { data: firstAlbumVideoData, ...gctlConfig } = useGetAlbumVideos(
    () => {
      if (!viewer || !shouldGetFirstVideo) return null;
      return {
        where: { albumId },
        select: SELECT_ALBUM_FIELDS,
        query: {
          perPage: 2,
          ...DEFAULT_SHOWCASE_QUERY,
          ...embedQuery,
          ...sortConfig,
          ...(password && { password }),
          ...(hideUpcoming && { filter: 'playable' }),
        },
        headers: { Accept: 'application/vnd.vimeo.*+json;version=3.4.12' },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const clip = firstAlbumVideoData?.data?.[0];
  let eventId;
  if (clip?.live?.status) {
    eventId = getEventIdFromClip(clip);
  }

  const { link: firstClipLink = '' } = clip?.link
    ? contextUrlParser(clip.link, { baseUrl, eventId })
    : {};

  return {
    firstClip: firstAlbumVideoData?.data?.[0],
    firstClipLink,
    ...gctlConfig,
  };
};
